import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { preStyleGuideTheme, setPostStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { RadioStyle } from "@insite/mobius/Radio";
import { TextAreaStyle } from "@insite/mobius/TextArea";
import { InputStyle } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import "@sunSource/Store/CustomSystemReducer";
import { css } from "styled-components";
/* import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components"; */

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
// Do not do this for the ./Overrides folder
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(require.context("./Widgets", true, /\.tsx$/));
if (module.hot) {
    module.hot.accept((widgets as any).id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

/* const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key)); */

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// in production, the widget extensions are included in the chunks created for widgets
// if (!IS_PRODUCTION) {
// load all widget extensions. They could be loaded individually instead
const widgetExtensions = require.context("./WidgetExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));
// }

// Add some post styleguide customizations. These cannot be overridden by the style guide.
setPostStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [576, 768, 768, 992, 1300],
    },
    typography: {
        fontFamilyImportUrl: "/staticContent/stylesheet.css",
        body: {
            size: "1rem",
            weight: 400,
            lineHeight: "24px",
            fontFamily: "OpenSans",
        },
        p: {
            size: "1rem", // 16px
            weight: 400,
            lineHeight: "25px",
        },
        h1: {
            size: "2rem", // 32px
            weight: 700,
            color: "text.accent",
            lineHeight: "40px",
            fontFamily: "Barlow-Regular",
        },
        h2: {
            size: "1.5rem", // 24px
            weight: 700,
            color: "text.accent",
            lineHeight: "34px",
            fontFamily: "OpenSans",
        },
        h3: {
            size: "1.25rem", // 20px
            weight: 600,
            color: "text.accent",
            lineHeight: "28px",
            fontFamily: "OpenSans",
        },
        h4: {
            size: "1.25rem", // 20px
            weight: 700,
            color: "text.accent",
            lineHeight: "28px",
            fontFamily: "OpenSans",
        },
        h5: {
            size: "1.125rem", // 18px
            weight: 700,
            color: "text.accent",
            lineHeight: "26px",
            fontFamily: "OpenSans",
        },
        h6: {
            size: "1rem",
            weight: 700,
            color: "text.accent",
            lineHeight: "24px",
            fontFamily: "OpenSans",
        },
        headerPrimary: {
            fontFamily: "FrutigerCn",
        },
        headerSecondary: {
            fontFamily: "FrutigerCn",
            weight: 600,
        },
        headerTertiary: {
            fontFamily: "OpenSans",
        },
    },
    focus: {
        style: "none",
    },
    button: {
        primary: {
            hoverStyle: {
                backgroundColor: "#262626",
                border: "2px solid #262626",
            },
        },
    },
    formField: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                    font-size: 1rem;
                    &:focus, &:focus-within{
                        border: 2px solid ${getColor("primary")} !important;
                    }
                `,
                formInputWrapper: css`
                    >${InputStyle}, >${TextAreaStyle}, select{
                        &:focus, &:focus-within{
                            border: 2px solid ${getColor("primary")} !important;
                        }
                    }
                    > div[class^="DateTimePickerStyle"] {
                        > div[class^="react-datetime-picker"] {
                            &:focus, &:focus-within {
                                border: 2px solid ${getColor("primary")} !important;
                            }
                        }
                    }
                    >div[class^="FileUploadStyle-sc"] {
                        &:focus-within, &:focus{
                            border: 2px solid ${getColor("primary")} !important;
                        }
                        input {
                            font-size: 1rem;
                            &:focus, &:focus-within {
                                border: 2px solid ${getColor("primary")} !important;
                            }
                        }
                    }
                `,
            },
            labelProps: {
                color: "secondary",
                size: 16,
                transform: "capitalize",
            },
            errorProps: {
                size: 14,
            },
        },
    },
    textField: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                    &:focus, &:focus-within {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                `,
            },
        },
    },
    textArea: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                &:focus, &:focus-within {
                    border: 2px solid ${getColor("primary")} !important;
                }
            `,
            },
        }
    },
    pagination: {
        defaultProps: {
            cssOverrides: {
                pagination: css`
                    border: 1px solid ${getColor("common.border")};
                    padding: 5px;
                    label {
                        font-weight: normal !important;
                        color: ${getColor("secondary")} !important;
                        font-size: 14px !important;
                        font-style: italic;
                    }
                    select {
                        font-size: 14px !important;
                        height: 36px;
                    }
                `,
                currentButton: css`
                    cursor: default;
                    background-color: ${getColor("primary")};
                    min-height: 30px;
                `,
                linkList: css`
                    button {
                        max-width: fit-content;
                        white-space: nowrap;
                        &:hover {
                            border: none !important;
                        }
                    }
                `,
            },
        },
    },
    toast: {
        defaultProps: {
            bodyTypographyProps: {
                color: "primary",
            },
            closeButtonProps: {
                css: css`
                    align-self: flex-start;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 45px;
                    height: 45px;
                    border-radius: 50%;
                    position: absolute;
                    top: 10px;
                    right: 10px;
                `,
            },
        },
    },
    accordion: {
        sectionDefaultProps: {
            headerProps: {
                css: css`
                    button {
                        background: ${getColor("info.contrast")};
                        border: 1px solid ${getColor("common.border")};
                        // margin-bottom: 8px;
                        > ${TypographyStyle} {
                            font-size: 16px;
                            font-weight: bold;
                        }
                        &:focus {
                            border: 1px solid ${getColor("common.border")};
                        }
                    }
                `,
            },
            panelProps: {
                css: css`
                    padding: 1.4rem 1rem;
                    border: 1px solid ${getColor("common.border")};
                    > ${GridWrapper} {
                        > ${GridOffset} {
                            > ${GridItemStyle} {
                                ${RadioStyle} {
                                    ${TypographyStyle} {
                                        font-size: 14px;
                                    }
                                }
                            }
                            > ${GridItemStyle}:last-child {
                                > ${TypographyStyle} {
                                    font-size: 1rem;
                                }
                            }
                        }
                    }
                `,
            },
            titleTypographyProps: {
                css: css`
                    color: ${getColor("secondary")};
                `,
            },
            toggleIconProps: {
                css: css`
                    color: ${getColor("secondary")};
                `,
            },
        },
    },
});
